/**
 * API client. Uses VITE_API_URL (empty = same-origin via Vite proxy).
 * `credentials: "include"` ensures the session cookie flows through.
 */
const RAW_BASE = String(import.meta.env.VITE_API_URL || "");
export const API_BASE = RAW_BASE.replace(/\/$/, "");

export class ApiError extends Error {
  code: string;
  status: number;
  details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${API_BASE}${path}`;
  const resp = await fetch(url, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!resp.ok) {
    const b = (body as { code?: string; message?: string; details?: unknown }) || {};
    throw new ApiError(resp.status, b.code || "UNKNOWN", b.message || resp.statusText, b.details);
  }
  return body as T;
}

// SIWE flow
export interface NonceResponse {
  nonce: string;
  issued_at: string;
  expires_at: string;
}

export interface VerifyResponse {
  ok: boolean;
  wallet: string;
  path: "eoa" | "erc1271" | "erc6492";
  expires_at: string;
}

export interface MeResponse {
  wallet: string;
  expires_at: string;
}

export const api = {
  nonce: () => request<NonceResponse>("/api/v1/auth/nonce", { method: "POST" }),
  verify: (message: string, signature: string) =>
    request<VerifyResponse>("/api/v1/auth/verify", {
      method: "POST",
      body: JSON.stringify({ message, signature }),
    }),
  me: () => request<MeResponse>("/api/v1/auth/me"),
  logout: () => request<{ ok: boolean }>("/api/v1/auth/logout", { method: "POST" }),
  health: () => request<Record<string, unknown>>("/api/health"),
};
